import { useEffect, useMemo, useRef, useState } from "react";
import styles from "./BackgroundMotion.module.scss";
import { createCurrent, createParticles, particleCountForTank } from "./current.js";

const SHAFTS = [
    { left: "26%", width: "11%", delay: "0s" },
    { left: "40%", width: "7%", delay: "-4s" },
    { left: "50%", width: "13%", delay: "-8s" },
    { left: "62%", width: "7%", delay: "-2s" },
    { left: "76%", width: "11%", delay: "-6s" },
];

const PARTICLE_POOL = 20;

function prefersReducedMotion() {
    if (typeof window.matchMedia !== "function") return false;
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function readTankSize(tank) {
    if (!tank) return { width: window.innerWidth, height: window.innerHeight };
    const { width, height } = tank.getBoundingClientRect();
    if (width < 1 || height < 1) return { width: window.innerWidth, height: window.innerHeight };
    return { width, height };
}

function BackgroundMotion() {
    const rootRef = useRef(null);
    const directionRef = useRef(Math.random() > 0.5 ? 1 : -1);
    const [tank, setTank] = useState(() => readTankSize(null));
    const [paused, setPaused] = useState(() => document.hidden);
    const [reduced, setReduced] = useState(prefersReducedMotion);
    const particles = useMemo(() => createParticles(PARTICLE_POOL), []);
    const current = createCurrent(tank.width, tank.height, directionRef.current);
    const visibleParticles = particles.slice(0, particleCountForTank(tank.width, tank.height));

    useEffect(() => {
        const aquarium = rootRef.current?.parentElement;
        if (!aquarium) return undefined;

        const measure = () => setTank(readTankSize(aquarium));
        measure();
        if (typeof ResizeObserver === "undefined") return undefined;
        const observer = new ResizeObserver(measure);
        observer.observe(aquarium);
        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        const onVisibility = () => setPaused(document.hidden);
        document.addEventListener("visibilitychange", onVisibility);
        return () => document.removeEventListener("visibilitychange", onVisibility);
    }, []);

    useEffect(() => {
        if (typeof window.matchMedia !== "function") return undefined;
        const media = window.matchMedia("(prefers-reduced-motion: reduce)");
        const onChange = () => setReduced(media.matches);
        media.addEventListener("change", onChange);
        return () => media.removeEventListener("change", onChange);
    }, []);

    return (
        <div
            ref={rootRef}
            className={`${styles.motion} ${paused ? styles.paused : ""} ${reduced ? styles.reduced : ""}`}
            style={{
                "--drift-x": `${current.driftX}px`,
                "--drift-y": `${current.driftY}px`,
                "--current-duration": `${current.duration}s`,
                "--sway": `${current.sway}deg`,
            }}
            aria-hidden="true"
        >
            {SHAFTS.map(shaft => (
                <div
                    key={shaft.left}
                    className={styles.shaft}
                    style={{
                        left: shaft.left,
                        width: shaft.width,
                        "--shaft-delay": shaft.delay,
                    }}
                />
            ))}
            <div className={styles.caustics} />
            <div className={`${styles.caustics} ${styles.causticsBack}`} />
            <div className={styles.shimmer} />
            {visibleParticles.map(particle => (
                <span
                    key={particle.id}
                    className={styles.particle}
                    style={{
                        left: `${particle.left}%`,
                        top: `${particle.top}%`,
                        width: particle.size,
                        height: particle.size,
                        opacity: particle.opacity,
                        "--particle-scale": particle.durationScale,
                        "--particle-delay": `${particle.delay}s`,
                    }}
                />
            ))}
        </div>
    );
}

export default BackgroundMotion;
